import type { SavedTargetingsManager } from '@/state/experiments/useSavedTargetings';
import { Bookmark, ChevronDown, Plus, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { summarizeSavedRules, truncateName } from '@/state/experiments/saved-targeting-summary';

type SavedTargetingMenuProps = { manager: SavedTargetingsManager };

const ITEM_CLASS = 'flex cursor-pointer flex-col items-start gap-0.5 rounded-lg px-3 py-2';

/**
 * The one entry point: save, update the entry this experiment was loaded from, load one of the
 * five most recent, or open the full list. Radix gives the WAI-ARIA menu button behaviour
 * (arrow keys, typeahead, Esc back to the button).
 */
export const SavedTargetingMenu = ({ manager }: SavedTargetingMenuProps) => {
  const { recent, source, editedSinceLoad } = manager;

  return (
    // modal={false}: every item opens a Dialog, and a modal menu closing under an opening Dialog
    // can leave pointer-events: none on the body.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="border-input h-10 rounded-lg bg-transparent">
          <Bookmark className="size-4" strokeWidth={1.5} />
          Saved targeting
          <ChevronDown className="size-4" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="border-line bg-panel w-[380px] max-w-[calc(100vw-2rem)] rounded-panel p-1.5"
      >
        <DropdownMenuItem className={ITEM_CLASS} onSelect={() => manager.openSave('new')}>
          <span className="flex items-center gap-2 text-body">
            <Plus className="size-4" strokeWidth={1.5} />
            Save as new…
          </span>
        </DropdownMenuItem>

        {source && editedSinceLoad ? (
          <DropdownMenuItem className={ITEM_CLASS} onSelect={() => manager.openSave('update')}>
            <span className="flex items-center gap-2 text-body">
              <RefreshCw className="size-4" strokeWidth={1.5} />
              Update "{truncateName(source.name)}"
            </span>
            <span className="supporting pl-6">Overwrites the saved copy with your edits.</span>
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuSeparator className="bg-line" />
        <DropdownMenuLabel className="micro-label px-3 pt-2 pb-1">Load · replaces current</DropdownMenuLabel>

        {recent.length === 0 ? (
          <DropdownMenuItem disabled className={ITEM_CLASS}>
            <span className="supporting">Nothing saved yet</span>
          </DropdownMenuItem>
        ) : (
          recent.map((entry) => (
            <DropdownMenuItem key={entry.id} className={ITEM_CLASS} onSelect={() => void manager.loadFromMenu(entry)}>
              <span className="flex w-full items-baseline justify-between gap-2">
                <span className="text-foreground truncate text-body">{entry.name}</span>
                {entry.id === source?.id ? <span className="supporting shrink-0">Current source</span> : null}
              </span>
              <span className="supporting truncate">{summarizeSavedRules(entry.rules)}</span>
            </DropdownMenuItem>
          ))
        )}

        <DropdownMenuSeparator className="bg-line" />
        <DropdownMenuItem className={ITEM_CLASS} onSelect={() => manager.openLoad()}>
          <span className="text-brand flex items-center gap-2 text-body">
            <Search className="size-4" strokeWidth={1.5} />
            Search all saved targeting…
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
